import React, { useState, useEffect } from "react";
import { get } from "../../services/api";

function WithdrawalSummaryModal({
  isOpen,
  onClose,
  organizationId,
  centerId,
  sessionId,
  classId,
  cityCode = "",
  stateCode = "",
}) {
  const [loading, setLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState([]);
  const [detailsData, setDetailsData] = useState([]);
  const [activeTab, setActiveTab] = useState("summary");
  const [centerSessions, setCenterSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedPrevSession, setSelectedPrevSession] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredDetails, setFilteredDetails] = useState([]);
  const [filteredGroupedData, setFilteredGroupedData] = useState({});

  // Fetch center sessions when modal opens
  useEffect(() => {
    if (isOpen && centerId !== undefined) {
      fetchCenterSessions();
    }
  }, [isOpen, centerId]);

  // Set default selected sessions when sessions are loaded
  useEffect(() => {
    if (centerSessions.length > 0) {
      // Set current session (default to first session)
      if (!selectedSession && centerSessions[0]) {
        const firstSession = centerSessions[0];
        setSelectedSession({
          value: firstSession.id,
          label: firstSession.sessionName || firstSession.name,
        });
      }

      // Set previous session (default to second session if available, otherwise first)
      if (!selectedPrevSession && centerSessions.length > 1) {
        const secondSession = centerSessions[0];
        setSelectedPrevSession({
          value: secondSession.id,
          label: secondSession.sessionName || secondSession.name,
        });
      } else if (!selectedPrevSession && centerSessions[0]) {
        const firstSession = centerSessions[0];
        setSelectedPrevSession({
          value: firstSession.id,
          label: firstSession.sessionName || firstSession.name,
        });
      }
    }
  }, [centerSessions]);

  // Fetch withdrawal report when sessions are selected
  useEffect(() => {
    if (isOpen && organizationId && selectedSession && selectedPrevSession) {
      fetchWithdrawalReport();
    }
  }, [isOpen, organizationId, selectedSession, selectedPrevSession]);

  useEffect(() => {
    // Filter details data when search query changes
    if (detailsData.length > 0) {
      if (searchQuery.trim() === "") {
        setFilteredDetails(detailsData);
      } else {
        const filtered = detailsData.filter((student) => {
          const query = searchQuery.toLowerCase();
          return (
            (student.rollNo &&
              student.rollNo.toString().toLowerCase().includes(query)) ||
            (student.studentName &&
              student.studentName.toLowerCase().includes(query)) ||
            (student.class && student.class.toLowerCase().includes(query)) ||
            (student.fatherName &&
              student.fatherName.toLowerCase().includes(query)) ||
            (student.fatherCnic &&
              student.fatherCnic.toLowerCase().includes(query)) ||
            (student.feeOverdue &&
              student.feeOverdue.toString().includes(query))
          );
        });
        setFilteredDetails(filtered);
      }
    }
  }, [searchQuery, detailsData]);

  useEffect(() => {
    // Filter grouped data when active tab changes to "grouped" or filteredDetails changes
    if (activeTab === "grouped" && filteredDetails.length > 0) {
      const grouped = {};
      filteredDetails.forEach((student) => {
        const className = student.class;
        if (!grouped[className]) {
          grouped[className] = [];
        }
        grouped[className].push(student);
      });
      setFilteredGroupedData(grouped);
    }
  }, [filteredDetails, activeTab]);

  // Fetch center sessions - Copied from StudentSummaryModal
  const fetchCenterSessions = async () => {
    setSessionsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await get(
        `FeePlan/GetCenterSessionsByOrganizationId?centerId=${
          centerId || 0
        }&&organizationId=${organizationId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response && response.isSuccess && response.centerSessions) {
        // Remove duplicates based on sessionName
        const uniqueSessions = response.centerSessions.reduce(
          (acc, session) => {
            if (!acc.find((s) => s.sessionName === session.sessionName)) {
              acc.push(session);
            }
            return acc;
          },
          []
        );

        // Sort sessions by sessionName in descending order (newest first)
        const sortedSessions = uniqueSessions.sort((a, b) =>
          b.sessionName.localeCompare(a.sessionName)
        );

        setCenterSessions(sortedSessions);
      }
    } catch (err) {
      console.error("Error fetching center sessions:", err);
    } finally {
      setSessionsLoading(false);
    }
  };

  const fetchWithdrawalReport = async () => {
    setLoading(true);
    setError(null);
    setSearchQuery("");
    setSearchOpen(false);

    try {
      const token = localStorage.getItem("token");
      const effectiveClassId = classId ?? 0;

      const response = await get(
        `Dashboard/GetWithdrawalStudentDashboardReport?organizationId=${organizationId}&centerId=${centerId}&classId=${effectiveClassId}&sessionId=${selectedSession.value}&prevSessionId=${selectedPrevSession.value}&cityCode=${cityCode}&stateCode=${stateCode}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response) {
        // Filter out "Total" from summary
        const filteredSummary = response.summary.filter(
          (item) => item.class !== "Total"
        );
        setSummaryData(filteredSummary);
        setDetailsData(response.details || []);
      }
    } catch (err) {
      console.error("Error fetching withdrawal report:", err);
      setError("Failed to load withdrawal report data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Function to export data to Excel
  const exportToExcel = () => {
    try {
      let dataToExport = [];
      let fileName = "";

      switch (activeTab) {
        case "summary":
          dataToExport = summaryData.map((item) => ({
            Class: item.class,
            Current_Withdrawals: item.currentStudents || 0,
            Previous_Withdrawals: item.previousStudents || 0,
            Variance:
              calculateVariance(item.currentStudents, item.previousStudents) +
              "%",
            Status:
              calculateVariance(item.currentStudents, item.previousStudents) > 0
                ? "Increase"
                : calculateVariance(
                    item.currentStudents,
                    item.previousStudents
                  ) < 0
                ? "Decrease"
                : "No Change",
          }));
          fileName = `Withdrawal_Summary_${centerId}_${
            selectedSession?.label
          }_vs_${selectedPrevSession?.label}_${
            new Date().toISOString().split("T")[0]
          }`;
          break;

        case "details":
          dataToExport = detailsData.map((student) => ({
            Roll_No: student.rollNo,
            Student_Name: student.studentName,
            Class: student.class,
            Withdrawal_Date: formatDate(
              student.withdrawalDate || student.admissionDate
            ),
            Father_Name: student.fatherName,
            Father_CNIC: student.fatherCnic,
            Fee_Overdue: student.feeOverdue || "",
          }));
          fileName = `Withdrawal_Details_${centerId}_${
            selectedSession?.label
          }_${new Date().toISOString().split("T")[0]}`;
          break;

        case "grouped":
          const grouped = groupStudentsByClass();
          dataToExport = [];
          // Get sorted class names using custom sorting
          const sortedClassNames = sortClassNames(Object.keys(grouped));
          sortedClassNames.forEach((className) => {
            grouped[className].forEach((student) => {
              dataToExport.push({
                Class: className,
                Roll_No: student.rollNo,
                Student_Name: student.studentName,
                Withdrawal_Date: formatDate(
                  student.withdrawalDate || student.admissionDate
                ),
                Father_Name: student.fatherName,
                Father_CNIC: student.fatherCnic,
                Fee_Overdue: student.feeOverdue || "",
              });
            });
          });
          fileName = `Withdrawals_Grouped_by_Class_${centerId}_${
            selectedSession?.label
          }_${new Date().toISOString().split("T")[0]}`;
          break;
      }

      if (dataToExport.length === 0) {
        alert("No data available to export!");
        return;
      }

      // Create CSV content
      const headers = Object.keys(dataToExport[0]).join(",");
      const rows = dataToExport.map((row) =>
        Object.values(row)
          .map((value) =>
            typeof value === "string" && value.includes(",")
              ? `"${value}"`
              : value
          )
          .join(",")
      );

      const csvContent = [headers, ...rows].join("\n");

      // Create and trigger download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute("download", `${fileName}.csv`);
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert("Failed to export data. Please try again.");
    }
  };

  if (!isOpen) return null;

  // Function to sort class names in proper order
  const sortClassNames = (classNames) => {
    // Define the order of classes
    const classOrder = [
      "Nursery",
      "Kindergarten",
      "KG",
      "KG-I",
      "KG-II",
      "KG-1",
      "KG-2",
      "Playgroup",
      "Pre-Nursery",
      "Pre-KG",
      "Montessori",
    ];

    // Add numeric classes (1-12)
    for (let i = 1; i <= 12; i++) {
      classOrder.push(`Class ${i}`, `Grade ${i}`, `Std ${i}`);
    }

    // Custom sorting function
    return classNames.sort((a, b) => {
      const aIndex = classOrder.findIndex(
        (order) =>
          a.toLowerCase().includes(order.toLowerCase()) ||
          order.toLowerCase().includes(a.toLowerCase())
      );
      const bIndex = classOrder.findIndex(
        (order) =>
          b.toLowerCase().includes(order.toLowerCase()) ||
          order.toLowerCase().includes(b.toLowerCase())
      );

      // If both classes are in the predefined order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // If only one class is in the predefined order
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      // Try to extract numeric values from class names
      const aNum = extractNumberFromClassName(a);
      const bNum = extractNumberFromClassName(b);

      if (aNum !== null && bNum !== null) {
        return aNum - bNum;
      }

      // Default string comparison
      return a.localeCompare(b);
    });
  };

  // Helper function to extract numeric value from class name
  const extractNumberFromClassName = (className) => {
    const match = className.match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
  };

  // Group students by class for the "Group by Class" tab
  const groupStudentsByClass = () => {
    const grouped = {};
    detailsData.forEach((student) => {
      const className = student.class;
      if (!grouped[className]) {
        grouped[className] = [];
      }
      grouped[className].push(student);
    });
    return grouped;
  };

  const groupedData = groupStudentsByClass();
  // Sort class names using custom sorting
  const classNames = sortClassNames(Object.keys(groupedData));

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return "-";
    }
  };

  const calculateVariance = (current, previous) => {
    if (previous === 0) return 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const getVarianceDisplay = (variance) => {
    if (variance > 0) {
      return {
        text: `+${variance}%`,
        color: "#ef4444", // Red for withdrawal increase
        bgColor: "#fee2e2",
        icon: "▲",
        label: "Increase",
      };
    } else if (variance < 0) {
      return {
        text: `${variance}%`,
        color: "#10b981", // Green for withdrawal decrease
        bgColor: "#d1fae5",
        icon: "▼",
        label: "Decrease",
      };
    } else {
      return {
        text: "0%",
        color: "#6b7280",
        bgColor: "#f3f4f6",
        icon: "●",
        label: "No Change",
      };
    }
  };

  const totals = {
    current: summaryData.reduce(
      (sum, item) => sum + (item.currentStudents || 0),
      0
    ),
    previous: summaryData.reduce(
      (sum, item) => sum + (item.previousStudents || 0),
      0
    ),
    variance: calculateVariance(
      summaryData.reduce((sum, item) => sum + (item.currentStudents || 0), 0),
      summaryData.reduce((sum, item) => sum + (item.previousStudents || 0), 0)
    ),
  };

  return (
    <div
      className="modal-backdrop"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "20px",
      }}
      onClick={onClose}
    >
      <div
        className="modal-content"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          width: "100%",
          maxWidth: "1350px", // Reduced from 1400px for better compression
          maxHeight: "90vh",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #e5e7eb",
            backgroundColor: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: 600,
                color: "#111827",
              }}
            >
              Withdrawal Students Report
            </h3>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginTop: "4px",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#6b7280",
                }}
              >
                Center ID: {centerId} • Comparing:{" "}
                {selectedSession?.label || "Loading..."} vs{" "}
                {selectedPrevSession?.label || "Loading..."}
              </p>
              <div
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#ef4444",
                  }}
                ></div>
                <span style={{ fontSize: "12px", color: "#6b7280" }}>
                  Total Withdrawals: {totals.current}
                </span>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                padding: "6px 12px",
                backgroundColor: "#ffffff",
                fontSize: "13px",
                minWidth: "180px",
              }}
            >
              <span style={{ color: "#6b7280" }}>Current</span>
              <select
                value={selectedSession?.value || ""}
                onChange={(e) => {
                  const sessionId = parseInt(e.target.value);
                  const session = centerSessions.find(
                    (s) => s.id === sessionId
                  );
                  if (session) {
                    setSelectedSession({
                      value: session.id,
                      label: session.sessionName || session.name,
                    });
                  }
                }}
                disabled={sessionsLoading || centerSessions.length === 0}
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: "13px",
                  color: "#111827",
                  fontWeight: 500,
                  cursor: "pointer",
                  flex: 1,
                  minWidth: "120px",
                }}
              >
                {sessionsLoading ? (
                  <option value="">Loading...</option>
                ) : centerSessions.length === 0 ? (
                  <option value="">No sessions</option>
                ) : (
                  centerSessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.sessionName}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Previous Session Dropdown - Same as above but for previous */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                padding: "6px 12px",
                backgroundColor: "#ffffff",
                fontSize: "13px",
                minWidth: "180px",
              }}
            >
              <span style={{ color: "#6b7280" }}>Previous</span>
              <select
                value={selectedPrevSession?.value || ""}
                onChange={(e) => {
                  const sessionId = parseInt(e.target.value);
                  const session = centerSessions.find(
                    (s) => s.id === sessionId
                  );
                  if (session) {
                    setSelectedPrevSession({
                      value: session.id,
                      label: session.sessionName || session.name,
                    });
                  }
                }}
                disabled={sessionsLoading || centerSessions.length === 0}
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: "13px",
                  color: "#111827",
                  fontWeight: 500,
                  cursor: "pointer",
                  flex: 1,
                  minWidth: "120px",
                }}
              >
                {sessionsLoading ? (
                  <option value="">Loading...</option>
                ) : centerSessions.length === 0 ? (
                  <option value="">No sessions</option>
                ) : (
                  centerSessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.sessionName}
                    </option>
                  ))
                )}
              </select>
            </div>

            <button
              onClick={onClose}
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f9fafb";
              }}
              onMouseLeave={(e) => {
                e.target.backgroundColor = "#ffffff";
              }}
            >
              <span style={{ fontSize: "18px", color: "#6b7280" }}>×</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            borderBottom: "1px solid #e5e7eb",
            backgroundColor: "#f9fafb",
            padding: "0 24px",
          }}
        >
          <button
            onClick={() => setActiveTab("summary")}
            style={{
              padding: "12px 16px",
              backgroundColor:
                activeTab === "summary" ? "#ffffff" : "transparent",
              border: "none",
              borderBottom:
                activeTab === "summary"
                  ? "2px solid #3b82f6"
                  : "2px solid transparent",
              color: activeTab === "summary" ? "#111827" : "#6b7280",
              fontWeight: activeTab === "summary" ? 600 : 500,
              fontSize: "14px",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            Summary View
          </button>
          <button
            onClick={() => setActiveTab("details")}
            style={{
              padding: "12px 16px",
              backgroundColor:
                activeTab === "details" ? "#ffffff" : "transparent",
              border: "none",
              borderBottom:
                activeTab === "details"
                  ? "2px solid #3b82f6"
                  : "2px solid transparent",
              color: activeTab === "details" ? "#111827" : "#6b7280",
              fontWeight: activeTab === "details" ? 600 : 500,
              fontSize: "14px",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            Details List
            <span
              style={{
                marginLeft: "6px",
                backgroundColor: "#e5e7eb",
                color: "#4b5563",
                padding: "1px 6px",
                borderRadius: "10px",
                fontSize: "11px",
                fontWeight: 500,
              }}
            >
              {detailsData.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("grouped")}
            style={{
              padding: "12px 16px",
              backgroundColor:
                activeTab === "grouped" ? "#ffffff" : "transparent",
              border: "none",
              borderBottom:
                activeTab === "grouped"
                  ? "2px solid #3b82f6"
                  : "2px solid transparent",
              color: activeTab === "grouped" ? "#111827" : "#6b7280",
              fontWeight: activeTab === "grouped" ? 600 : 500,
              fontSize: "14px",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            Group by Class
            <span
              style={{
                marginLeft: "6px",
                backgroundColor: "#e5e7eb",
                color: "#4b5563",
                padding: "1px 6px",
                borderRadius: "10px",
                fontSize: "11px",
                fontWeight: 500,
              }}
            >
              {classNames.length}
            </span>
          </button>
        </div>

        {/* Modal Body - Rest of the content remains the same */}
        {/* ... باقی کوڈ StudentSummaryModal.js سے ملتا جلتا ہے ... */}

        {/* Only the table headers need to show the session labels */}
        <div
          style={{
            padding: "0",
            overflowY: "auto",
            flex: 1,
            backgroundColor: "#ffffff",
          }}
        >
          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "300px",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  border: "3px solid #f3f4f6",
                  borderTop: "3px solid #3b82f6",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
              <p style={{ color: "#6b7280", fontSize: "14px" }}>
                Loading withdrawal report...
              </p>
            </div>
          ) : error ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "300px",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <div style={{ fontSize: "40px", color: "#ef4444" }}>⚠️</div>
              <p style={{ color: "#ef4444", fontSize: "14px" }}>{error}</p>
              <button
                onClick={fetchWithdrawalReport}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Retry
              </button>
            </div>
          ) : activeTab === "summary" ? (
            <div style={{ padding: "24px" }}>
              {/* Summary Stats Cards with Icons */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "16px",
                  marginBottom: "24px",
                }}
              >
                {/* Current Session Withdrawals Card */}
                <div
                  style={{
                    backgroundColor: "#fef2f2",
                    padding: "16px",
                    borderRadius: "8px",
                    border: "1px solid #fecaca",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>
                      <p
                        style={{
                          margin: "0 0 4px 0",
                          fontSize: "13px",
                          color: "#dc2626",
                          fontWeight: 500,
                        }}
                      >
                        Current Session ({selectedSession?.label || "N/A"})
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "24px",
                          color: "#991b1b",
                          fontWeight: 700,
                        }}
                      >
                        {totals.current}
                      </p>
                    </div>
                    <div
                      style={{
                        fontSize: "28px",
                        color: "#ef4444",
                        opacity: 0.8,
                      }}
                    >
                      <i className="fas fa-user-slash"></i>
                    </div>
                  </div>
                </div>

                {/* Previous Session Withdrawals Card */}
                <div
                  style={{
                    backgroundColor: "#fef3c7",
                    padding: "16px",
                    borderRadius: "8px",
                    border: "1px solid #fde68a",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>
                      <p
                        style={{
                          margin: "0 0 4px 0",
                          fontSize: "13px",
                          color: "#92400e",
                          fontWeight: 500,
                        }}
                      >
                        Previous Session ({selectedPrevSession?.label || "N/A"})
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "24px",
                          color: "#78350f",
                          fontWeight: 700,
                        }}
                      >
                        {totals.previous}
                      </p>
                    </div>
                    <div
                      style={{
                        fontSize: "28px",
                        color: "#f59e0b",
                        opacity: 0.8,
                      }}
                    >
                      <i className="fas fa-history"></i>
                    </div>
                  </div>
                </div>

                {/* Withdrawal Change Card */}
                <div
                  style={{
                    backgroundColor:
                      totals.variance >= 0 ? "#fee2e2" : "#d1fae5",
                    padding: "16px",
                    borderRadius: "8px",
                    border:
                      totals.variance >= 0
                        ? "1px solid #fecaca"
                        : "1px solid #a7f3d0",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          margin: "0 0 4px 0",
                          fontSize: "13px",
                          color: totals.variance >= 0 ? "#991b1b" : "#065f46",
                          fontWeight: 500,
                        }}
                      >
                        Overall Variance
                      </p>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <p
                          style={{
                            margin: 0,
                            fontSize: "24px",
                            color: totals.variance >= 0 ? "#991b1b" : "#065f46",
                            fontWeight: 700,
                          }}
                        >
                          {totals.variance >= 0 ? "+" : ""}
                          {totals.variance}%
                        </p>
                        {/* Trend Arrow */}
                        {totals.variance > 0 ? (
                          <i
                            className="fas fa-arrow-up"
                            style={{
                              fontSize: "16px",
                              color: "#991b1b",
                            }}
                          ></i>
                        ) : totals.variance < 0 ? (
                          <i
                            className="fas fa-arrow-down"
                            style={{
                              fontSize: "16px",
                              color: "#065f46",
                            }}
                          ></i>
                        ) : (
                          <i
                            className="fas fa-minus"
                            style={{
                              fontSize: "16px",
                              color: "#6b7280",
                            }}
                          ></i>
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "28px",
                        color: totals.variance >= 0 ? "#ef4444" : "#10b981",
                        opacity: 0.8,
                        marginLeft: "10px",
                      }}
                    >
                      <i
                        className={
                          totals.variance >= 0
                            ? "fas fa-chart-line-up"
                            : "fas fa-chart-line-down"
                        }
                      ></i>
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary Table with reduced column gaps */}
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#f9fafb" }}>
                      <th
                        style={{
                          padding: "12px 10px",
                          textAlign: "left",
                          fontWeight: 600,
                          color: "#374151",
                          borderBottom: "1px solid #e5e7eb",
                          minWidth: "80px",
                        }}
                      >
                        Class
                      </th>
                      <th
                        style={{
                          padding: "12px 10px",
                          textAlign: "center",
                          fontWeight: 600,
                          color: "#374151",
                          borderBottom: "1px solid #e5e7eb",
                          minWidth: "100px",
                        }}
                      >
                        Current ({selectedSession?.label || "N/A"})
                      </th>
                      <th
                        style={{
                          padding: "12px 10px",
                          textAlign: "center",
                          fontWeight: 600,
                          color: "#374151",
                          borderBottom: "1px solid #e5e7eb",
                          minWidth: "100px",
                        }}
                      >
                        Previous ({selectedPrevSession?.label || "N/A"})
                      </th>
                      <th
                        style={{
                          padding: "12px 10px",
                          textAlign: "center",
                          fontWeight: 600,
                          color: "#374151",
                          borderBottom: "1px solid #e5e7eb",
                          minWidth: "90px",
                        }}
                      >
                        Variance %
                      </th>
                      <th
                        style={{
                          padding: "12px 10px",
                          textAlign: "center",
                          fontWeight: 600,
                          color: "#374151",
                          borderBottom: "1px solid #e5e7eb",
                          minWidth: "80px",
                        }}
                      >
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryData.length === 0 ? (
                      <tr>
                        <td
                          colSpan="5"
                          style={{
                            padding: "40px",
                            textAlign: "center",
                            color: "#9ca3af",
                          }}
                        >
                          No withdrawal summary data available
                        </td>
                      </tr>
                    ) : (
                      summaryData.map((item, index) => {
                        const variance = calculateVariance(
                          item.currentStudents,
                          item.previousStudents
                        );
                        const varianceDisplay = getVarianceDisplay(variance);

                        return (
                          <tr
                            key={index}
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#ffffff" : "#f9fafb",
                              borderBottom: "1px solid #e5e7eb",
                            }}
                          >
                            <td
                              style={{
                                padding: "12px 10px",
                                fontWeight: 500,
                                color: "#111827",
                              }}
                            >
                              {item.class}
                            </td>
                            <td
                              style={{
                                padding: "12px 10px",
                                textAlign: "center",
                              }}
                            >
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "6px 10px",
                                  backgroundColor:
                                    item.currentStudents > 0
                                      ? "#fee2e2"
                                      : "#f3f4f6",
                                  color:
                                    item.currentStudents > 0
                                      ? "#dc2626"
                                      : "#6b7280",
                                  borderRadius: "6px",
                                  fontWeight: 600,
                                  minWidth: "35px",
                                }}
                              >
                                {item.currentStudents || 0}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "12px 10px",
                                textAlign: "center",
                              }}
                            >
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "6px 10px",
                                  backgroundColor:
                                    item.previousStudents > 0
                                      ? "#fef3c7"
                                      : "#f3f4f6",
                                  color:
                                    item.previousStudents > 0
                                      ? "#92400e"
                                      : "#6b7280",
                                  borderRadius: "6px",
                                  fontWeight: 600,
                                  minWidth: "35px",
                                }}
                              >
                                {item.previousStudents || 0}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "12px 10px",
                                textAlign: "center",
                                fontWeight: 600,
                                color: varianceDisplay.color,
                              }}
                            >
                              {varianceDisplay.text}
                            </td>
                            <td
                              style={{
                                padding: "12px 10px",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "28px",
                                  height: "28px",
                                  backgroundColor: varianceDisplay.bgColor,
                                  color: varianceDisplay.color,
                                  borderRadius: "50%",
                                  fontSize: "14px",
                                  fontWeight: 600,
                                }}
                              >
                                {varianceDisplay.icon}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : activeTab === "details" ? (
            <div style={{ padding: "24px" }}>
              {/* Details Table with reduced column gaps */}
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "16px",
                    backgroundColor: "#f9fafb",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        color: "#374151",
                        fontWeight: 600,
                      }}
                    >
                      Withdrawal Details ({filteredDetails.length} records)
                    </p>
                    {searchOpen ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <div style={{ position: "relative" }}>
                          <i
                            className="fas fa-search"
                            style={{
                              position: "absolute",
                              left: "10px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#9ca3af",
                              fontSize: "14px",
                            }}
                          ></i>
                          <input
                            type="text"
                            placeholder="Search in all columns..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                              padding: "8px 12px 8px 34px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              fontSize: "13px",
                              width: "300px",
                              outline: "none",
                              transition: "all 0.2s",
                            }}
                            onFocus={(e) =>
                              (e.target.style.borderColor = "#3b82f6")
                            }
                            onBlur={(e) =>
                              (e.target.style.borderColor = "#d1d5db")
                            }
                          />
                        </div>
                        <button
                          onClick={() => {
                            setSearchQuery("");
                            setSearchOpen(false);
                          }}
                          style={{
                            padding: "8px 12px",
                            backgroundColor: "#f3f4f6",
                            color: "#374151",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: 500,
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <i className="fas fa-times"></i> Clear
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setSearchOpen(true)}
                        style={{
                          padding: "8px 12px",
                          backgroundColor: "#3b82f6",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "13px",
                          fontWeight: 500,
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor = "#2563eb")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "#3b82f6")
                        }
                      >
                        <i className="fas fa-search"></i> Search
                      </button>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    {searchQuery && (
                      <span style={{ fontSize: "12px", color: "#6b7280" }}>
                        Found: {filteredDetails.length} of {detailsData.length}
                      </span>
                    )}
                    <p
                      style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}
                    >
                      Updated: {new Date().toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "13px",
                      minWidth: "900px",
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: "#f9fafb" }}>
                        <th
                          style={{
                            padding: "10px 8px",
                            textAlign: "left",
                            fontWeight: 600,
                            color: "#374151",
                            borderBottom: "1px solid #e5e7eb",
                            minWidth: "80px",
                          }}
                        >
                          Roll No
                        </th>
                        <th
                          style={{
                            padding: "10px 8px",
                            textAlign: "left",
                            fontWeight: 600,
                            color: "#374151",
                            borderBottom: "1px solid #e5e7eb",
                            minWidth: "120px",
                          }}
                        >
                          Student Name
                        </th>
                        <th
                          style={{
                            padding: "10px 8px",
                            textAlign: "left",
                            fontWeight: 600,
                            color: "#374151",
                            borderBottom: "1px solid #e5e7eb",
                            minWidth: "80px",
                          }}
                        >
                          Class
                        </th>
                        <th
                          style={{
                            padding: "10px 8px",
                            textAlign: "left",
                            fontWeight: 600,
                            color: "#374151",
                            borderBottom: "1px solid #e5e7eb",
                            minWidth: "100px",
                          }}
                        >
                          Withdrawal Date
                        </th>
                        <th
                          style={{
                            padding: "10px 8px",
                            textAlign: "left",
                            fontWeight: 600,
                            color: "#374151",
                            borderBottom: "1px solid #e5e7eb",
                            minWidth: "120px",
                          }}
                        >
                          Father Name
                        </th>
                        <th
                          style={{
                            padding: "10px 8px",
                            textAlign: "left",
                            fontWeight: 600,
                            color: "#374151",
                            borderBottom: "1px solid #e5e7eb",
                            minWidth: "120px",
                          }}
                        >
                          Father CNIC
                        </th>
                        <th
                          style={{
                            padding: "10px 8px",
                            textAlign: "left",
                            fontWeight: 600,
                            color: "#374151",
                            borderBottom: "1px solid #e5e7eb",
                            minWidth: "100px",
                          }}
                        >
                          Fee Overdue
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailsData.length === 0 ? (
                        <tr>
                          <td
                            colSpan="7"
                            style={{
                              padding: "40px",
                              textAlign: "center",
                              color: "#9ca3af",
                            }}
                          >
                            No withdrawal records available
                          </td>
                        </tr>
                      ) : (
                        filteredDetails.map((student, index) => (
                          <tr
                            key={index}
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#ffffff" : "#f9fafb",
                              borderBottom: "1px solid #e5e7eb",
                            }}
                          >
                            <td
                              style={{
                                padding: "10px 8px",
                                fontWeight: 600,
                                color: "#dc2626",
                              }}
                            >
                              {student.rollNo}
                            </td>
                            <td
                              style={{
                                padding: "10px 8px",
                                color: "#111827",
                              }}
                            >
                              {student.studentName}
                            </td>
                            <td
                              style={{
                                padding: "10px 8px",
                                color: "#111827",
                              }}
                            >
                              <span
                                style={{
                                  padding: "4px 6px",
                                  backgroundColor: "#fee2e2",
                                  color: "#dc2626",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                }}
                              >
                                {student.class}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "10px 8px",
                                color: "#6b7280",
                              }}
                            >
                              {formatDate(
                                student.withdrawalDate || student.admissionDate
                              )}
                            </td>
                            <td
                              style={{
                                padding: "10px 8px",
                                color: "#111827",
                              }}
                            >
                              {student.fatherName}
                            </td>
                            <td
                              style={{
                                padding: "10px 8px",
                                color: "#6b7280",
                                fontFamily: "monospace",
                              }}
                            >
                              {student.fatherCnic}
                            </td>
                            <td
                              style={{
                                padding: "10px 8px",
                                color: "#6b7280",
                              }}
                            >
                              {student.feeOverdue || ""}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ padding: "24px" }}>
              {/* Group by Class View with reduced column gaps */}
              <div style={{ marginBottom: "16px" }}>
                <p
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "14px",
                    color: "#6b7280",
                  }}
                >
                  Showing {classNames.length} classes with {detailsData.length}{" "}
                  withdrawal students
                </p>
              </div>

              {classNames.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#9ca3af",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    backgroundColor: "#f9fafb",
                  }}
                >
                  <div style={{ fontSize: "64px", marginBottom: "16px" }}>
                    📭
                  </div>
                  <p style={{ margin: 0, fontSize: "14px", fontWeight: 500 }}>
                    No withdrawal records found
                  </p>
                  <p
                    style={{
                      margin: "8px 0 0 0",
                      fontSize: "12px",
                      color: "#9ca3af",
                    }}
                  >
                    There are no student withdrawals for the selected session
                  </p>
                </div>
              ) : (
                classNames.map((className, classIndex) => {
                  const classStudents = groupedData[className];
                  const classSummary = summaryData.find(
                    (item) => item.class === className
                  );

                  return (
                    <div
                      key={classIndex}
                      style={{
                        marginBottom: "20px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        overflow: "hidden",
                      }}
                    >
                      {/* Class Header */}
                      <div
                        style={{
                          padding: "14px 16px",
                          backgroundColor: "#fef2f2",
                          borderBottom: "1px solid #e5e7eb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div>
                          <h4
                            style={{
                              margin: 0,
                              fontSize: "15px",
                              fontWeight: 600,
                              color: "#dc2626",
                            }}
                          >
                            {className}
                          </h4>
                          <p
                            style={{
                              margin: "4px 0 0 0",
                              fontSize: "12px",
                              color: "#ef4444",
                            }}
                          >
                            {classStudents.length} withdrawal student
                            {classStudents.length !== 1 ? "s" : ""}
                            {classSummary && (
                              <span
                                style={{ marginLeft: "10px", color: "#9ca3af" }}
                              >
                                • Current: {classSummary.currentStudents} •
                                Previous: {classSummary.previousStudents}
                              </span>
                            )}
                          </p>
                        </div>
                        {classSummary && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <div
                              style={{
                                padding: "4px 8px",
                                backgroundColor: "#fee2e2",
                                color: "#dc2626",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontWeight: 500,
                              }}
                            >
                              Current: {classSummary.currentStudents}
                            </div>
                            <div
                              style={{
                                padding: "4px 8px",
                                backgroundColor: "#fef3c7",
                                color: "#92400e",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontWeight: 500,
                              }}
                            >
                              Previous: {classSummary.previousStudents}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Students Table for this Class */}
                      <div style={{ overflowX: "auto" }}>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: "13px",
                          }}
                        >
                          <thead>
                            <tr style={{ backgroundColor: "#f9fafb" }}>
                              <th
                                style={{
                                  padding: "10px 8px",
                                  textAlign: "left",
                                  fontWeight: 600,
                                  color: "#374151",
                                  borderBottom: "1px solid #e5e7eb",
                                  minWidth: "80px",
                                }}
                              >
                                Roll No
                              </th>
                              <th
                                style={{
                                  padding: "10px 8px",
                                  textAlign: "left",
                                  fontWeight: 600,
                                  color: "#374151",
                                  borderBottom: "1px solid #e5e7eb",
                                  minWidth: "120px",
                                }}
                              >
                                Student Name
                              </th>
                              <th
                                style={{
                                  padding: "10px 8px",
                                  textAlign: "left",
                                  fontWeight: 600,
                                  color: "#374151",
                                  borderBottom: "1px solid #e5e7eb",
                                  minWidth: "100px",
                                }}
                              >
                                Withdrawal Date
                              </th>
                              <th
                                style={{
                                  padding: "10px 8px",
                                  textAlign: "left",
                                  fontWeight: 600,
                                  color: "#374151",
                                  borderBottom: "1px solid #e5e7eb",
                                  minWidth: "120px",
                                }}
                              >
                                Father Name
                              </th>
                              <th
                                style={{
                                  padding: "10px 8px",
                                  textAlign: "left",
                                  fontWeight: 600,
                                  color: "#374151",
                                  borderBottom: "1px solid #e5e7eb",
                                  minWidth: "120px",
                                }}
                              >
                                Father CNIC
                              </th>
                              <th
                                style={{
                                  padding: "10px 8px",
                                  textAlign: "left",
                                  fontWeight: 600,
                                  color: "#374151",
                                  borderBottom: "1px solid #e5e7eb",
                                  minWidth: "100px",
                                }}
                              >
                                Fee Overdue
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {classStudents.map((student, studentIndex) => (
                              <tr
                                key={studentIndex}
                                style={{
                                  backgroundColor:
                                    studentIndex % 2 === 0
                                      ? "#ffffff"
                                      : "#f9fafb",
                                  borderBottom:
                                    studentIndex === classStudents.length - 1
                                      ? "none"
                                      : "1px solid #e5e7eb",
                                }}
                              >
                                <td
                                  style={{
                                    padding: "10px 8px",
                                    fontWeight: 600,
                                    color: "#dc2626",
                                  }}
                                >
                                  {student.rollNo}
                                </td>
                                <td
                                  style={{
                                    padding: "10px 8px",
                                    color: "#111827",
                                  }}
                                >
                                  {student.studentName}
                                </td>
                                <td
                                  style={{
                                    padding: "10px 8px",
                                    color: "#6b7280",
                                  }}
                                >
                                  {formatDate(
                                    student.withdrawalDate ||
                                      student.admissionDate
                                  )}
                                </td>
                                <td
                                  style={{
                                    padding: "10px 8px",
                                    color: "#111827",
                                  }}
                                >
                                  {student.fatherName}
                                </td>
                                <td
                                  style={{
                                    padding: "10px 8px",
                                    color: "#6b7280",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {student.fatherCnic}
                                </td>
                                <td
                                  style={{
                                    padding: "10px 8px",
                                    color: "#6b7280",
                                  }}
                                >
                                  {student.feeOverdue || ""}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Modal Footer with Export Button */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
            Report generated on {new Date().toLocaleString()}
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button
              onClick={() => window.print()}
              style={{
                padding: "8px 16px",
                backgroundColor: "#f3f4f6",
                color: "#374151",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span>🖨️</span> Print Report
            </button>
            <button
              onClick={exportToExcel}
              style={{
                padding: "8px 16px",
                backgroundColor: "#10b981",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <i className="fas fa-file-excel"></i> Export to Excel
            </button>
            <button
              onClick={onClose}
              style={{
                padding: "8px 20px",
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <style jsx="true">{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .modal-content {
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Scrollbar styling */
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        /* Print styles to ensure columns don't get cut off */
        @media print {
          body * {
            visibility: hidden;
          }
          .modal-content,
          .modal-content * {
            visibility: visible;
          }
          .modal-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            max-width: 100%;
            box-shadow: none;
          }
          button {
            display: none !important;
          }
          .modal-backdrop {
            background-color: white;
            backdrop-filter: none;
          }
        }
      `}</style>
    </div>
  );
}

export default WithdrawalSummaryModal;
